/*
 * Cookie consent. Spec: docs/cookie-consent-spec.md.
 *
 * This is a custom BANNER, not a custom consent MECHANISM. Every choice is written through
 * Shopify.customerPrivacy.setTrackingConsent(), the same API Shopify's own banner calls, so
 * consent is stored in Shopify's own cookie and honoured by every pixel, app and Web Pixel
 * on the shop. We are replacing the presentation layer only.
 *
 * FAIL-SAFE, and the single most important rule in this file:
 *   Shopify's default banner is hidden from JAVASCRIPT, and only after this script has
 *   confirmed the consent API is live. If this file fails to load, throws, or the API never
 *   arrives, Shopify's banner stays visible and the store stays compliant. Hiding it from CSS
 *   would mean one JS error leaves an EEA store with no consent mechanism at all.
 */
(function () {
  'use strict';

  var BANNER_ID = 'KardimaCookieBanner';
  var PREFS_ID = 'KardimaCookiePrefs';

  var banner = null;
  var prefs = null;
  var panel = null;
  var api = null;
  var lastFocused = null;

  /* ---- Shopify API bootstrap -------------------------------------------- */

  /*
   * The consent API is loaded asynchronously. Shopify.loadFeatures is the documented way to
   * wait for it; the poll is a fallback for the window where Shopify.loadFeatures itself has
   * not been defined yet. Both paths converge on the same onReady.
   */
  function whenApiReady(onReady, onGiveUp) {
    var shopify = window.Shopify;

    if (shopify && shopify.customerPrivacy) {
      onReady(shopify.customerPrivacy);
      return;
    }

    if (shopify && typeof shopify.loadFeatures === 'function') {
      shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (error || !window.Shopify || !window.Shopify.customerPrivacy) {
            onGiveUp();
            return;
          }
          onReady(window.Shopify.customerPrivacy);
        }
      );
      return;
    }

    // Shopify's own script has not run yet. Poll briefly, then give up quietly.
    var waited = 0;
    var timer = setInterval(function () {
      waited += 100;
      if (window.Shopify && window.Shopify.customerPrivacy) {
        clearInterval(timer);
        onReady(window.Shopify.customerPrivacy);
      } else if (waited >= 6000) {
        clearInterval(timer);
        onGiveUp();
      }
    }, 100);
  }

  /*
   * Hide Shopify's default banner + preferences dialog.
   *
   * This runs EARLY (first thing in init), not after the consent API resolves, because waiting
   * meant Shopify's English banner could paint for a moment before ours replaced it — a visible
   * flash of the wrong language on every first visit.
   *
   * The fail-safe is preserved by unhide() below rather than by delaying: if the consent API
   * never arrives, we put Shopify's banner back. And if this file fails to load or parse at all,
   * nothing is ever injected, so Shopify's banner is untouched.
   */
  function hideShopifyBanner() {
    if (document.getElementById('kardima-cc-hide-default')) return;
    var style = document.createElement('style');
    style.id = 'kardima-cc-hide-default';
    /*
     * SUBSTRING selectors, not exact class names, and that is the whole point.
     *
     * The first version of this hid `.shopify-pc__banner`. No such element exists — Shopify's
     * top-level element is `section.shopify-pc__banner__dialog`, and a CSS class selector is an
     * exact match, so `.shopify-pc__banner` matched ZERO elements. Result: both banners showed
     * at once for real customers, while our side looked fine in testing because consent was
     * already stored in the test session so Shopify's banner never rendered to contradict it.
     *
     * `[class*="shopify-pc__banner"]` catches __dialog, __wrapper, __body, __btns and anything
     * Shopify renames them to later.
     */
    style.textContent =
      '[class*="shopify-pc__banner"],[class*="shopify-pc__prefs"],' +
      '#shopify-pc__banner,#shopify-pc__prefs{display:none !important;}';
    (document.head || document.documentElement).appendChild(style);
  }

  function unhideShopifyBanner() {
    var style = document.getElementById('kardima-cc-hide-default');
    if (style) style.remove();
  }

  /* ---- Consent ----------------------------------------------------------- */

  /*
   * sale_of_data is bound to the marketing toggle rather than given its own row. It is a
   * CCPA/CPRA concept with no separate meaning under GDPR, and a fourth switch a Norwegian
   * customer cannot act on meaningfully is friction with no benefit. See the spec.
   */
  function writeConsent(choice) {
    if (!api || typeof api.setTrackingConsent !== 'function') return;

    api.setTrackingConsent(
      {
        analytics: !!choice.analytics,
        marketing: !!choice.marketing,
        preferences: !!choice.preferences,
        sale_of_data: !!choice.marketing
      },
      function (result) {
        // Shopify reports failures on the result object rather than by throwing.
        if (result && result.error) {
          console.warn('[kardima-cc] setTrackingConsent failed:', result.error);
          return;
        }
        closeBanner();
        closePrefs();
      }
    );
  }

  function acceptAll() {
    writeConsent({ analytics: true, marketing: true, preferences: true });
  }

  function rejectAll() {
    writeConsent({ analytics: false, marketing: false, preferences: false });
  }

  function saveFromToggles() {
    writeConsent({
      analytics: isChecked('analytics'),
      marketing: isChecked('marketing'),
      preferences: isChecked('preferences')
    });
  }

  function isChecked(category) {
    var input = prefs.querySelector('[data-cc-category="' + category + '"]');
    return !!(input && input.checked);
  }

  /*
   * Has this visitor actually answered yet?
   *
   * MEASURED, and it is a trap: Shopify's shouldShowBanner() keeps returning TRUE even after
   * setTrackingConsent() has stored a full accept-all and userCanBeTracked() reports true.
   * It tracks whether SHOPIFY's own banner was dismissed, and our custom banner never dismisses
   * it. Gating on shouldShowBanner() alone showed the banner on every page load, forever.
   *
   * currentVisitorConsent() is the honest signal: each category is '' until answered, then
   * 'yes' or 'no'. Necessary cookies are not a category and are never checked here.
   */
  function consentAlreadyGiven() {
    if (!api || typeof api.currentVisitorConsent !== 'function') return false;
    var current = api.currentVisitorConsent() || {};
    return ['analytics', 'marketing', 'preferences'].every(function (category) {
      return current[category] === 'yes' || current[category] === 'no';
    });
  }

  /* Reflect the visitor's existing choices when the panel is reopened from the footer. */
  function syncTogglesFromConsent() {
    if (!api || typeof api.currentVisitorConsent !== 'function') return;

    var current = api.currentVisitorConsent() || {};
    ['analytics', 'marketing', 'preferences'].forEach(function (category) {
      var input = prefs.querySelector('[data-cc-category="' + category + '"]');
      if (input) input.checked = current[category] === 'yes';
    });
  }

  /* ---- Banner open/close ------------------------------------------------- */

  function openBanner() {
    banner.hidden = false;
    // Force layout so the transition has a starting frame to animate from.
    void banner.offsetWidth;
    banner.setAttribute('data-open', '');
  }

  function closeBanner() {
    if (!banner || banner.hidden) return;
    banner.removeAttribute('data-open');
    afterTransition(banner, function () {
      banner.hidden = true;
    });
  }

  /* ---- Preferences panel ------------------------------------------------- */

  function openPrefs() {
    lastFocused = document.activeElement;
    syncTogglesFromConsent();

    prefs.hidden = false;
    void panel.offsetWidth;
    prefs.setAttribute('data-open', '');
    document.body.classList.add('overflow-hidden');

    var close = prefs.querySelector('[data-cc-prefs-close]');
    if (close) close.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closePrefs() {
    if (!prefs || prefs.hidden) return;

    prefs.removeAttribute('data-open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', onKeydown);

    afterTransition(panel, function () {
      prefs.hidden = true;
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape') closePrefs();
  }

  /*
   * transitionend does not fire under prefers-reduced-motion (the transition is removed
   * entirely), so every transition wait needs a timeout fallback or the element would stay
   * in the DOM forever. Same pattern as kardima-size-guide.js.
   */
  function afterTransition(element, done) {
    var finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      done();
    }
    element.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 400);
  }

  /* ---- Wiring ------------------------------------------------------------ */

  function bind() {
    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-cc-accept]')) {
        acceptAll();
      } else if (target.closest('[data-cc-reject]')) {
        rejectAll();
      } else if (target.closest('[data-cc-save]')) {
        saveFromToggles();
      } else if (target.closest('[data-cc-open-prefs]') || target.closest('[data-cc-reopen]')) {
        openPrefs();
      } else if (target.closest('[data-cc-prefs-close]')) {
        closePrefs();
      } else if (target === prefs) {
        /*
         * The scrim is `.kardima-cc-prefs::before`. A pseudo-element has no event target of its
         * own — a click on it reports the HOST element — so `target === prefs` means the scrim
         * and nothing else. Clicks inside the panel report panel descendants and fall through.
         */
        closePrefs();
      }
    });
  }

  function init() {
    banner = document.getElementById(BANNER_ID);
    prefs = document.getElementById(PREFS_ID);
    if (!banner || !prefs) return;

    panel = prefs.querySelector('.kardima-cc-prefs__panel');
    if (!panel) return;

    bind();

    // Suppress Shopify's banner up front to avoid a flash; restored below if we cannot proceed.
    hideShopifyBanner();

    whenApiReady(
      function (customerPrivacy) {
        api = customerPrivacy;

        /*
         * Two conditions, both required:
         *   shouldShowBanner()  - Shopify's region check (false where no consent is required),
         *                         so we never nag customers outside a regulated region.
         *   !consentAlreadyGiven() - our own check, because shouldShowBanner() never flips back
         *                         to false for a custom banner. See the note above.
         */
        var regionRequiresConsent =
          typeof api.shouldShowBanner === 'function' ? api.shouldShowBanner() : false;

        if (regionRequiresConsent && !consentAlreadyGiven()) openBanner();
      },
      function () {
        /*
         * The API never arrived, so our buttons could not record anything. Put Shopify's own
         * banner back and leave ours hidden — a working default banner beats a pretty one that
         * silently discards the visitor's choice.
         */
        unhideShopifyBanner();
        console.warn('[kardima-cc] Customer Privacy API unavailable; restored Shopify default banner.');
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
